import React, { useCallback, useEffect, useState } from 'react';
import { whatsappAPI } from './whatsappAPI';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { QrCode, LogOut, RefreshCw, CheckCircle2, XCircle, Loader2, AlertTriangle, RotateCw, Smartphone } from 'lucide-react';

const INFO = {
  connected:       { icon: CheckCircle2, cor: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', titulo: 'Conectado' },
  qr:              { icon: QrCode,       cor: 'text-amber-600',   bg: 'bg-amber-50 border-amber-200',     titulo: 'Aguardando leitura do QR Code' },
  connecting:      { icon: Loader2,      cor: 'text-amber-600',   bg: 'bg-amber-50 border-amber-200',     titulo: 'Conectando…', spin: true },
  reconnecting:    { icon: Loader2,      cor: 'text-amber-600',   bg: 'bg-amber-50 border-amber-200',     titulo: 'Reconectando…', spin: true },
  conflict:        { icon: AlertTriangle, cor: 'text-red-600',    bg: 'bg-red-50 border-red-200',         titulo: 'Sessão aberta em outro lugar' },
  logged_out:      { icon: XCircle,      cor: 'text-red-600',     bg: 'bg-red-50 border-red-200',         titulo: 'Desconectado — escaneie o QR novamente' },
  service_offline: { icon: XCircle,      cor: 'text-red-600',     bg: 'bg-red-50 border-red-200',         titulo: 'Serviço WhatsApp fora do ar' },
  offline:         { icon: XCircle,      cor: 'text-slate-500',   bg: 'bg-slate-50 border-slate-200',     titulo: 'Não conectado' },
};

function desde(iso) {
  if (!iso) return '';
  const s = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (s < 60) return `há ${s}s`;
  if (s < 3600) return `há ${Math.round(s / 60)} min`;
  if (s < 86400) return `há ${Math.round(s / 3600)} h`;
  return `há ${Math.round(s / 86400)} dias`;
}
const fmtPhone = (n = '') => (n.length >= 12 ? `+${n.slice(0, 2)} ${n.slice(2, 4)} ${n.slice(4, -4)}-${n.slice(-4)}` : `+${n}`);

export default function ConexaoWhatsApp() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(null); // 'start' | 'restart' | 'logout'

  const poll = useCallback(async () => {
    try { const r = await whatsappAPI.sessionStatus(); setStatus(r.data); } catch (_) { /* noop */ }
  }, []);

  const s = status?.status || 'offline';
  // Poll rápido só quando algo está mudando (QR expira em ~20s); conectado não precisa de 3s
  const intervalo = ['qr', 'connecting', 'reconnecting'].includes(s) ? 2500 : 10000;
  useEffect(() => {
    poll();
    const t = setInterval(() => { if (!document.hidden) poll(); }, intervalo);
    return () => clearInterval(t);
  }, [poll, intervalo]);

  const acao = async (tipo, fn, okMsg) => {
    setLoading(tipo);
    try { await fn(); if (okMsg) toast.success(okMsg); await poll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Operação falhou'); }
    finally { setLoading(null); }
  };
  const start = () => acao('start', whatsappAPI.sessionStart, 'Aguarde o QR Code aparecer');
  const restart = () => acao('restart', whatsappAPI.sessionRestart, 'Reconectando (a sessão é mantida)');
  const logout = () => {
    if (!window.confirm('Desconectar o WhatsApp deste sistema?\nSerá preciso escanear o QR Code novamente.')) return;
    acao('logout', whatsappAPI.sessionLogout, 'Desconectado');
  };

  const info = INFO[s] || INFO.offline;
  const Icon = info.icon;

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="wa-conexao-page">
      <Card className="p-6">
        <div className={`flex items-center gap-4 rounded-xl border p-4 ${info.bg}`}>
          <Icon className={`w-10 h-10 shrink-0 ${info.cor} ${info.spin ? 'animate-spin' : ''}`} />
          <div className="min-w-0">
            <h2 className={`text-lg font-bold ${info.cor}`}>{info.titulo}</h2>
            <p className="text-sm text-slate-600">
              {status?.numero && <><Smartphone className="inline w-3.5 h-3.5 -mt-0.5" /> {fmtPhone(status.numero)} · </>}
              {status?.since ? `desde ${desde(status.since)}` : 'verificando…'}
              {s === 'reconnecting' && status?.retries > 0 && ` · tentativa ${status.retries}`}
            </p>
            {status?.last_error && s !== 'connected' && (
              <p className="text-xs text-slate-500 mt-1 font-mono truncate" title={status.last_error}>Último erro: {status.last_error}</p>
            )}
          </div>
        </div>

        {s === 'qr' && status?.qr && (
          <div className="text-center py-6" data-testid="wa-qr-code">
            <img src={status.qr} alt="QR Code" className="mx-auto w-64 h-64 border rounded-lg shadow" />
            <ol className="text-sm text-slate-600 mt-4 space-y-1 inline-block text-left list-decimal pl-5">
              <li>Abra o WhatsApp no celular do número de atendimento</li>
              <li>Toque em <b>Configurações → Aparelhos conectados</b></li>
              <li>Toque em <b>Conectar um aparelho</b> e aponte para este código</li>
            </ol>
            <p className="text-xs text-slate-400 mt-2">O código se renova sozinho a cada ~20 segundos.</p>
          </div>
        )}

        {s === 'conflict' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4 text-sm text-red-900 space-y-2">
            <p><b>Outra instância do sistema abriu esta mesma sessão</b> e o WhatsApp desconectou esta aqui.
              Para não ficarem se derrubando, a reconexão automática foi pausada.</p>
            <p>Verifique no servidor se há mais de um serviço rodando com a mesma pasta de sessão:</p>
            <code className="block bg-red-100 p-2 rounded text-xs">ps aux | grep whatsapp-service</code>
            <p>Depois de parar o duplicado, clique em <b>Reconectar agora</b>.</p>
          </div>
        )}

        {s === 'service_offline' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4 text-sm text-red-900">
            O serviço WhatsApp não está respondendo no servidor. Verifique:
            <code className="block mt-2 bg-red-100 p-2 rounded text-xs">systemctl status agrolink-whatsapp{'\n'}tail -n 50 /var/log/agrolink-whatsapp.log</code>
          </div>
        )}

        {s === 'reconnecting' && (
          <p className="text-sm text-slate-600 my-4">
            A conexão caiu e o sistema está tentando voltar sozinho, com intervalos crescentes. As mensagens recebidas
            nesse período chegam assim que a conexão voltar. Se demorar mais que alguns minutos, use <b>Reconectar agora</b>.
          </p>
        )}

        <div className="flex flex-wrap gap-2 pt-4 mt-4 border-t">
          {['offline', 'logged_out', 'service_offline'].includes(s) && (
            <Button onClick={start} disabled={!!loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-start-btn">
              {loading === 'start' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <QrCode className="w-4 h-4 mr-1" />} Conectar número
            </Button>
          )}
          {['reconnecting', 'connecting', 'conflict', 'connected'].includes(s) && (
            <Button onClick={restart} disabled={!!loading} variant={s === 'connected' ? 'outline' : 'default'} data-testid="wa-restart-btn">
              {loading === 'restart' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCw className="w-4 h-4 mr-1" />}
              {s === 'connected' ? 'Reiniciar conexão' : 'Reconectar agora'}
            </Button>
          )}
          {['connected', 'reconnecting', 'conflict', 'qr'].includes(s) && (
            <Button onClick={logout} variant="destructive" disabled={!!loading} data-testid="wa-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Desconectar número
            </Button>
          )}
          <Button onClick={poll} variant="ghost" disabled={!!loading} className="ml-auto">
            <RefreshCw className="w-4 h-4 mr-1" /> Atualizar
          </Button>
        </div>
        {s === 'connected' && (
          <p className="text-xs text-slate-400 mt-2">
            "Reiniciar conexão" mantém a sessão (não pede QR). "Desconectar número" remove o aparelho e exige novo QR.
          </p>
        )}
      </Card>

      <Card className="p-4 bg-amber-50 dark:bg-amber-900/10 border-amber-200">
        <h3 className="font-semibold text-sm mb-2">⚠️ Boas práticas para evitar bloqueio do número</h3>
        <ul className="text-xs space-y-1 list-disc pl-4 text-muted-foreground">
          <li>Use um número <b>dedicado</b> ao atendimento (nunca o pessoal do dono)</li>
          <li>Priorize <b>responder</b> clientes; evite disparos em massa para quem nunca falou com você</li>
          <li>Abra o WhatsApp no celular do número <b>pelo menos a cada 14 dias</b>: se o celular ficar inativo, o WhatsApp desconecta os aparelhos vinculados</li>
          <li>Esta conexão usa o WhatsApp Web (não oficial): se o número for bloqueado, será preciso conectar outro</li>
        </ul>
      </Card>
    </div>
  );
}
